// chill-points-bus: ChillPoints loyalty event bus + balance/tier API.
//
// Sprint 3 Phase 3A foundation. Singleton sitewide. Captures every
// reward-eligible action as a `chillandout:event:<verb>` event AND queues it
// in localStorage for Phase 3 Worker drain (the Worker writes to the actual
// ChillPoints ledger via Shopify Admin API). Until the Worker is alive, the
// queue is the store of record — no events are lost.
//
// Per MASTER-BRIEF §11.1:
// Earn rules:
//   $1 spent           1 pt     (settings.chillpoints_earn_per_dollar)
//   profile completion 100 pts  (one-shot, fired on first post-signup load)
//   first review       50 pts   (legacy — we use reactions, see Phase 3B)
//   referral           500/250  (Phase 4+)
//   birthday           200      (annual, requires customer.metafields.chillandout.dob)
//   outfit shared      25       (Phase 3D)
//   design submission  200      (Phase 4 / TeeInBlue)
//   wishlist save      5        (settings.chillpoints_earn_per_save — chill addition)
//   reaction           5        (settings.chillpoints_earn_per_reaction — chill addition)
//
// Tier thresholds (read from customer.metafields.chillandout.points):
//   visitor       0–99
//   member        100–999       sage badge
//   regular       1000–4999     sage frame + mono name
//   inner-circle  5000–24999    bloom accent
//   designer      25000+        iridescent badge
//
// Spec: docs/MASTER-BRIEF.md §11.1, docs/PHASE-ROADMAP.md (Phase 4 = Sprint 3)
// Voice: docs/VOICE-EXAMPLES.md
// Budget: < 2.5 KB gzip. No deps.

(function () {
  'use strict';

  if (window.chillandoutPoints) return; // singleton

  const QUEUE_KEY        = 'chillandout:points:queue';
  const SHOWN_TOAST_KEY  = 'chillandout:points:toast-seen';
  const EVENT_PREFIX     = 'chillandout:event:';
  const POINTS_CHANGED   = 'chillandout:points:changed';
  const QUEUE_FLUSH      = 'chillandout:points:queue-flush';

  // Tier thresholds — keep mirrored with sections/profile-header.liquid Liquid
  // tier mapping. If you change here, change there too (and vice versa).
  const TIERS = [
    { id: 'designer',     min: 25000 },
    { id: 'inner-circle', min: 5000  },
    { id: 'regular',      min: 1000  },
    { id: 'member',       min: 100   },
    { id: 'visitor',      min: 0     },
  ];

  // Earn rules — defaults are per MASTER-BRIEF §11.1; merchant overrides via
  // theme settings. Values get injected into window.chillandoutPointsConfig
  // by snippets/scripts.liquid before this script loads.
  const DEFAULT_EARN = {
    save:     5,
    reaction: 5,
    outfit:   25,
    review:   50,
    profile:  100,
    birthday: 200,
    design:   200,
    referrer: 500,
    referee:  250,
    perDollar: 1,
  };

  // ---------- queue layer ----------

  function readQueue() {
    try {
      const raw = localStorage.getItem(QUEUE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  function writeQueue(entries) {
    try {
      // Soft cap to prevent unbounded growth on quiet-mode browsers (private
      // mode, storage quota errors). 200 events should cover a heavy session.
      const trimmed = entries.length > 200 ? entries.slice(-200) : entries;
      localStorage.setItem(QUEUE_KEY, JSON.stringify(trimmed));
    } catch { /* skip */ }
  }

  function enqueue(verb, points, payload) {
    const entry = {
      verb,
      points,
      payload: payload || {},
      timestamp: new Date().toISOString(),
    };
    const queue = readQueue();
    queue.push(entry);
    writeQueue(queue);
    return entry;
  }

  // ---------- balance + tier ----------

  function readBalance() {
    // Server-rendered balance is in <body data-points="...">. Bridge updates
    // it via DOM mutation when an event fires. The actual ledger source-of-
    // truth is customer.metafields.chillandout.points (server-side).
    const body = document.body;
    if (!body) return 0;
    const raw = body.dataset.chillPointsBalance ||
      (document.querySelector('[data-chill-profile-header]')?.dataset.points) ||
      '0';
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  function computeTier(balance) {
    for (const t of TIERS) {
      if (balance >= t.min) return t.id;
    }
    return 'visitor';
  }

  function readEarnRate(verb) {
    const cfg = window.chillandoutPointsConfig || {};
    const map = {
      save:      cfg.earnPerSave     ?? DEFAULT_EARN.save,
      reaction:  cfg.earnPerReaction ?? DEFAULT_EARN.reaction,
      outfit:    cfg.earnPerOutfit   ?? DEFAULT_EARN.outfit,
      review:    cfg.earnPerReview   ?? DEFAULT_EARN.review,
      profile:   cfg.earnProfile     ?? DEFAULT_EARN.profile,
      birthday:  cfg.earnBirthday    ?? DEFAULT_EARN.birthday,
      design:    cfg.earnDesign      ?? DEFAULT_EARN.design,
      referrer:  cfg.earnReferrer    ?? DEFAULT_EARN.referrer,
      referee:   cfg.earnReferee     ?? DEFAULT_EARN.referee,
      perDollar: cfg.earnPerDollar   ?? DEFAULT_EARN.perDollar,
    };
    return Number.isFinite(map[verb]) ? map[verb] : 0;
  }

  function isEnabled() {
    const cfg = window.chillandoutPointsConfig || {};
    return cfg.enabled === true;
  }

  function isLoggedIn() {
    return !!document.body?.dataset.customerId;
  }

  // ---------- public API ----------
  //
  // Components call this after a reward-eligible action.
  //   chillandoutPoints.emit('wishlist:saved', { productId: 123 })
  //   chillandoutPoints.emit('outfit:shared', { outfitId: 'abc' })
  //
  // Returns the queued entry { verb, points, payload, timestamp } so callers
  // can toast or update UI immediately. Even when ChillPoints is disabled at
  // the merchant level (cfg.enabled=false), events still emit + queue — the
  // queue captures the actions for when the merchant flips the feature on.

  function emit(verb, payload) {
    if (typeof verb !== 'string' || !verb) return null;

    // Resolve points value from a verb shorthand (e.g., 'wishlist:saved'
    // resolves to earn rate 'save'; 'reaction:added' resolves to 'reaction').
    const earnKey = mapVerbToEarnKey(verb);
    const points = isEnabled() && isLoggedIn() ? readEarnRate(earnKey) : 0;

    const entry = enqueue(verb, points, payload);

    // Dispatch the namespaced event so components can react.
    document.dispatchEvent(new CustomEvent(EVENT_PREFIX + verb, {
      detail: { points, payload: entry.payload, timestamp: entry.timestamp },
    }));

    // Also dispatch the aggregate balance-changed event so the points pill
    // and tier badge can refresh. We don't update the BODY dataset balance
    // optimistically — that's the server's job. The pill animates a +N flash
    // without committing to a new total.
    if (points > 0) {
      document.dispatchEvent(new CustomEvent(POINTS_CHANGED, {
        detail: { delta: points, verb, balance: readBalance() },
      }));
    }

    return entry;
  }

  function mapVerbToEarnKey(verb) {
    // Map full event verbs to earn-rate keys.
    const head = verb.split(':')[0];
    const tail = verb.split(':').slice(1).join(':');
    if (head === 'wishlist' && tail === 'saved') return 'save';
    if (head === 'reaction' && tail === 'added') return 'reaction';
    if (head === 'outfit' && tail === 'shared') return 'outfit';
    if (head === 'review' && tail === 'posted') return 'review';
    if (head === 'profile' && tail === 'completed') return 'profile';
    if (head === 'birthday') return 'birthday';
    if (head === 'design' && tail === 'submitted') return 'design';
    if (head === 'referral' && tail === 'sent') return 'referrer';
    if (head === 'referral' && tail === 'received') return 'referee';
    return head; // fallback
  }

  // ---------- subscription helpers ----------

  function onChange(callback) {
    if (typeof callback !== 'function') return function () {};
    const handler = (e) => callback(e.detail);
    document.addEventListener(POINTS_CHANGED, handler);
    return function unsubscribe() {
      document.removeEventListener(POINTS_CHANGED, handler);
    };
  }

  // ---------- Phase 3 hook ----------
  //
  // The Worker drains this queue on connection. Until then, flush() is a
  // no-op-with-defer that preserves the Promise contract.

  function flush() {
    const cfg = window.chillandoutPointsConfig || {};
    if (!cfg.transport || cfg.transport === 'queue') {
      return Promise.resolve({ ok: true, drained: 0, deferred: true });
    }
    // Worker transport implementation lands in Phase 3.
    return Promise.resolve({ ok: false, error: 'transport not implemented' });
  }

  function getStatus() {
    const queue = readQueue();
    return {
      enabled: isEnabled(),
      loggedIn: isLoggedIn(),
      transport: (window.chillandoutPointsConfig || {}).transport || 'queue',
      queueDepth: queue.length,
      balance: readBalance(),
      tier: computeTier(readBalance()),
    };
  }

  // ---------- expose ----------

  window.chillandoutPoints = {
    emit,
    onChange,
    flush,
    getBalance: readBalance,
    getTier: () => computeTier(readBalance()),
    getStatus,
    // Constants exposed for tests + introspection.
    QUEUE_KEY,
    EVENT_PREFIX,
    POINTS_CHANGED,
    QUEUE_FLUSH,
    TIERS,
    DEFAULT_EARN,
    _mapVerbToEarnKey: mapVerbToEarnKey,
    _readEarnRate: readEarnRate,
  };

  // ---------- earning event sources (auto-wire) ----------
  //
  // The bus listens for already-shipped chill events and re-emits them as
  // namespaced ChillPoints events with earn-rate-resolved payload. This way,
  // Phase 3A doesn't require touching every caller — wishlist save already
  // emits chillandout:wishlist:changed; we just translate.

  function onWishlistChanged(e) {
    const detail = (e && e.detail) || {};
    if (detail.action !== 'add') return; // earn only on net-new save
    if (!isLoggedIn()) return; // guests can't accrue (no customer to credit)
    emit('wishlist:saved', {
      productId: detail.productId,
      source: detail.source || 'click',
      handle: detail.handle || null,
    });
  }

  document.addEventListener('chillandout:wishlist:changed', onWishlistChanged);
})();
